import { Skeleton } from "@mui/material";
import { MD5 } from "crypto-js";
import { closeSnackbar, enqueueSnackbar } from "notistack";

export const formatDate = (date) => {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const suffixes = ["th", "st", "nd", "rd"];

  const d = new Date(date);

  const day = d.getDate();
  const daySuffix =
    day % 10 <= 3 && (day < 11 || day > 13) ? suffixes[day % 10] : suffixes[0];

  const month = months[d.getMonth()];
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const seconds = String(d.getSeconds()).padStart(2, "0");

  return `${month}, ${day}${daySuffix}, ${year} ${hours}:${minutes}:${seconds}`;
};

export const handleSearchData = (columns, data, search) => {
  const searchTerm = String(search).toLowerCase().trim();

  const searchableFields = columns.map((ele) => ele?.field);

  if (!searchTerm || searchTerm.trim() === "") {
    return data;
  }

  const filtered = data.filter((item) => {
    return searchableFields.some((field) => {
      const value = item[field];

      if (value === null || value === undefined) {
        return false;
      }

      return value.toString().toLowerCase().includes(searchTerm);
    });
  });

  return filtered || [];
};

export const generateAlias = (text = "") => {
  if (!text) {
    return "";
  }

  return text.trim().toLowerCase().replace(/\s+/g, "_");
};

export function getZonedDate(input, timeZone) {
  if (input === null || input === undefined || input === "") {
    return null;
  }

  const date =
    input instanceof Date
      ? input
      : typeof input === "string" || typeof input === "number"
        ? new Date(input)
        : null;

  if (!date || isNaN(date.getTime())) {
    return null;
  }

  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);

  const map = {};

  for (const p of parts) {
    if (p.type !== "literal") {
      map[p.type] = p.value;
    }
  }

  return new Date(
    `${map.year}-${map.month}-${map.day}T${map.hour}:${map.minute}:${map.second}`,
  );
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export function formatWithPattern(date, pattern) {
  const pad = (n) => String(n).padStart(2, "0");

  const hours24 = date.getHours();
  const hours12 = hours24 % 12 || 12;
  const ampm = hours24 >= 12 ? "pm" : "am";

  const tokens = {
    YYYY: String(date.getFullYear()),
    MM: pad(date.getMonth() + 1),
    MMM: MONTHS[date.getMonth()],
    DD: pad(date.getDate()),
    ddd: WEEKDAYS[date.getDay()],
    HH: pad(hours24),
    h: String(hours12),
    mm: pad(date.getMinutes()),
    ss: pad(date.getSeconds()),
    a: ampm,
    A: ampm.toUpperCase(),
  };

  return pattern.replace(
    /YYYY|MMM|MM|DD|ddd|HH|h|mm|ss|A|a/g,
    (m) => tokens[m],
  );
}

// formate datetime zone and use inside custom hook
export const formatUserDateTime = (
  value,
  timezone = "Asia/Kolkata",
  dateFormat = "YYYY-MM-DD",
  timeFormat = "HH:mm",
) => {
  if (!value) {
    return "-";
  }

  const zoned = getZonedDate(new Date(value), timezone);

  if (!zoned) {
    return "-";
  }

  const formattedDate = formatWithPattern(zoned, dateFormat);
  const formattedTime = formatWithPattern(zoned, timeFormat);

  return `${formattedDate} ${formattedTime}`;
};

export const ChipColor = (value) => {
  value = typeof value === "string" ? value.toLowerCase() : value;

  if (["online", "up", "done", true].includes(value)) {
    return "success";
  }

  if (["offline", "down", "failed", false].includes(value)) {
    return "error";
  }

  if (value === "partially done") {
    return "warning";
  }

  return "info";
};

export const TextColor = (message) => {
  switch (message) {
    case "Done":
      return "#2E7D32";

    case "Partial Done":
      return "#ED6C02";

    case "Executing":
      return "#1A237E";

    default:
      return "#D32F2F";
  }
};

export const formatDuration = (start, end) => {
  if (!start || !end) return "-";

  try {
    const startMs = new Date(start).getTime();
    const endMs = new Date(end).getTime();

    let diffSec = Math.floor(Math.abs(endMs - startMs) / 1000);

    const days = Math.floor(diffSec / (24 * 3600));
    diffSec %= 24 * 3600;

    const hours = Math.floor(diffSec / 3600);
    diffSec %= 3600;

    const minutes = Math.floor(diffSec / 60);
    const seconds = diffSec % 60;

    const parts = [];

    if (days) parts.push(`${days}d`);
    if (hours) parts.push(`${hours}h`);
    if (minutes) parts.push(`${minutes}m`);
    if (seconds || parts.length === 0) parts.push(`${seconds}s`);

    return parts.join(" ");
  } catch {
    return "-";
  }
};

const STORAGE_PREFIX = "pagination_";

export const clearAllPaginationState = () => {
  try {
    const keysToRemove = [];

    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);

      if (key?.startsWith(STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // ignore storage errors
  }
};

export function notify(message, variant = "default", options) {
  return enqueueSnackbar(message, {
    variant,
    ...options,
  });
}

export function dismiss(key) {
  closeSnackbar(key);
}

export const getDashboardErrorMessage = (
  rawMessage,
  entityName = "Dashboard Template",
) => {
  if (!rawMessage) {
    return "Something went wrong";
  }

  if (
    typeof rawMessage === "string" &&
    rawMessage.includes("E11000 duplicate key error")
  ) {
    return `${entityName} name already exists`;
  }

  return rawMessage;
};

//Unique Tab Id Generate
export function slugifyTabId(label) {
  const base = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

  return `${base || "tab"}-${Date.now()}`;
}
export const generateDummyObjectId = () => {
  const hex = '0123456789abcdef';
  let id = '';

  for (let i = 0; i < 24; i++) {
    id += hex[Math.floor(Math.random() * 16)];
  }

  return id;
};
